import { computed, ref, watch } from "vue";
import { FilterContext, useFiltersStore } from "@/_store/filters.module";
import { useReportsStore } from "@/_store/reports.module";
import { REPORT_USERS_SEARCH_MIN_LENGTH } from "@/constants/reports.ts";

const CONTEXT = FilterContext.WARNING_ONLY_EMAIL_EVENTS_REPORT_USERS;

export function useWarningEmailEventsUsers() {
  const filtersStore = useFiltersStore();
  const reportsStore = useReportsStore();

  // The submitted term lives in the filters store so it survives navigation; the field binds to a
  // local ref so that typing alone never triggers a request.
  const searchTerm = ref(filtersStore.filters[CONTEXT].search ?? "");

  const appliedSearch = computed(() => filtersStore.filters[CONTEXT].search ?? "");

  const users = computed(() => reportsStore.warningOnlyEmailEventsReport?.userDetails ?? []);

  const usersPage = computed(() => reportsStore.warningOnlyEmailEventsReport?.userDetailsPage);

  const totalUsers = computed(() => usersPage.value?.totalElements ?? 0);

  const totalPages = computed(() => usersPage.value?.totalPages ?? 0);

  const currentPage = computed(() => reportsStore.warningOnlyEmailEventsUsersPage);

  const hasPagination = computed(() => totalPages.value > 1);

  const isSearchTermValid = computed(() => {
    const trimmed = searchTerm.value.trim();
    return trimmed.length === 0 || trimmed.length >= REPORT_USERS_SEARCH_MIN_LENGTH;
  });

  const applySearch = async (term: string) => {
    filtersStore.setFilters(CONTEXT, { search: term });
    await reportsStore.searchWarningOnlyEmailEventsReportUsers();
  };

  const submitSearch = async () => {
    if (!isSearchTermValid.value) return;

    const trimmed = searchTerm.value.trim();
    // Re-submitting the same term would only repeat the request that produced what is on screen.
    if (trimmed === appliedSearch.value) return;

    await applySearch(trimmed);
  };

  const clearSearch = async () => {
    searchTerm.value = "";
    if (!appliedSearch.value) return;

    await applySearch("");
  };

  const goToPage = async (page: number) => {
    if (page === currentPage.value || page < 0 || page >= totalPages.value) return;

    await reportsStore.setWarningOnlyEmailEventsUsersPage(page);
  };

  // The report is rebuilt when the time frame changes, and the new period has its own user list, so
  // the current page index no longer means anything and the table restarts at the first page.
  watch(
    () => reportsStore.selectedTimeFrame,
    () => {
      reportsStore.warningOnlyEmailEventsUsersPage = 0;
    }
  );

  return {
    searchTerm,
    appliedSearch,
    isSearchTermValid,
    users,
    usersPage,
    totalUsers,
    totalPages,
    currentPage,
    hasPagination,
    submitSearch,
    clearSearch,
    goToPage,
  };
}
